import { useState } from 'react';
import { useApi } from '../context';

interface Entry {
  id: string;
  displayName: string;
  userPrincipalName: string;
}

/**
 * Search the Teams directory and pick an entry. `kind` "resource" lists Teams Phone
 * resource accounts (call queues, auto attendants and bot lines - Teams does not expose
 * which is which, so pick by name); "user" lists people.
 */
export function DirectoryPicker({
  label,
  onPick,
  defaultKind = 'resource',
}: {
  label: string;
  onPick: (entry: Entry, kind: 'user' | 'resource') => void;
  /** Which directory to search first (the user can switch). */
  defaultKind?: 'user' | 'resource';
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'user' | 'resource'>(defaultKind);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function search() {
    setBusy(true);
    setError(null);
    try {
      setResults(await api.get<Entry[]>(`/api/admin/directory/search?kind=${kind}&q=${encodeURIComponent(q)}`));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <button className="link" onClick={() => setOpen(true)}>{label}</button>;
  }
  return (
    <div className="picker">
      <div className="filters">
        <select value={kind} onChange={(e) => { setKind(e.target.value as 'user' | 'resource'); setResults(null); }}>
          <option value="resource">Queues &amp; auto attendants (resource accounts)</option>
          <option value="user">Users</option>
        </select>
        <input
          placeholder={kind === 'user' ? 'Name or email (min 2 chars)' : 'Search by any word in the name (optional)'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <button onClick={search} disabled={busy}>{busy ? 'Searching…' : 'Search'}</button>
        <button className="link" onClick={() => { setOpen(false); setResults(null); }}>close</button>
      </div>
      {kind === 'resource' && (
        <p className="hint">
          Auto attendants and call queues are listed under their <strong>resource account</strong> names
          (Teams admin centre → the attendant/queue → <em>Resource accounts</em> tab), not the AA/CQ name —
          e.g. "AA - IT Support" appears as its account "442034750279 - IT SUPPORT". Pick the account
          that carries the number callers should be routed through.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      {results && (
        <ul className="picker-results">
          {results.map((r) => (
            <li key={r.id}>
              <button className="link" onClick={() => { onPick(r, kind); setOpen(false); setResults(null); }}>
                {r.displayName}
              </button>{' '}
              <span className="sub">{r.userPrincipalName}</span>
            </li>
          ))}
          {results.length === 0 && <li><em>No matches</em></li>}
        </ul>
      )}
    </div>
  );
}
